import { Link } from 'react-router-dom'
import type { ProjectClaim } from '../../types'
import ClaimScorePill from './ClaimScorePill'
import PriorArtRuler from './PriorArtRuler'

/**
 * The focused claim (figure S1's right-hand drawer, `?focus=claim:N`).
 *
 * It answers one question — does THIS claim hold — with the four things needed
 * to judge it: the score on the 0-100 scale, the disclosure it was scored
 * against, the prior art nearest to it on that same scale, and the analysis's
 * own reason. Every one of them arrives on the claims payload; the drawer
 * computes nothing.
 *
 * Each supporting document is a link into the Evidence lens, because "which
 * documents carry this claim" and "what does this document establish" are the
 * two directions of one edge, and a reader who has just read the claim is one
 * click from the document that carries it.
 */
export interface ClaimDrawerProps {
  claim: ProjectClaim
  /** 1-based position and the total, exactly as the heading reads. */
  index: number
  total: number
  documentNames?: Record<string, string>
  onClose: () => void
}

export default function ClaimDrawer({ claim, index, total, documentNames, onClose }: ClaimDrawerProps) {
  const ids = claim.supporting_document_ids
  const scoredAgainst =
    ids.length > 1
      ? 'Scored against all of them together — no single document asserts this claim, so reading any one of them alone makes it look unsupported.'
      : ids.length === 1
        ? 'Scored against that document’s disclosure.'
        : 'Nothing in this invention’s documents carries this claim, so there was no disclosure to score it against.'

  return (
    <aside
      data-part="ClaimDrawer"
      className="rounded-xl border border-iflow-200 bg-white p-4 shadow-sm"
      aria-label={`Claim ${index} of ${total}`}
    >
      <div className="mb-1.5 flex items-center justify-between">
        <h3 className="text-[15px] font-extrabold text-iflow-900">
          Claim {index} of {total}
        </h3>
        <button
          type="button"
          onClick={onClose}
          title="Close — back to the claim list"
          aria-label="Close the claim"
          className="flex h-6.5 w-6.5 items-center justify-center rounded-lg border border-sky-200 bg-white p-1 text-gray-500 hover:text-gray-700"
        >
          ✕
        </button>
      </div>

      <p className="mb-2 border-l-[3px] border-iflow-300 py-px pl-3 text-[12px] leading-relaxed text-gray-700">
        {claim.claim}
      </p>

      <div className="mb-2.5 flex flex-wrap items-center gap-2">
        <ClaimScorePill score={claim.novelty_score} size="lg" />
        {claim.novelty_score != null && <span className="text-[11.5px] text-gray-500">on the 0–100 scale</span>}
        {claim.score_adjusted && (
          <span
            className="whitespace-nowrap rounded-full border border-sky-200 bg-sky-50 px-2 py-0.5 text-[11px] font-semibold text-iflow-700"
            title={
              claim.original_novelty_score != null
                ? `Score adjusted — the analysis lowered it from ${claim.original_novelty_score}`
                : 'Score adjusted — the analysis capped it'
            }
          >
            Score adjusted
          </span>
        )}
      </div>

      <div className="mb-3 rounded-xl border border-sky-200 bg-slate-50 px-3 py-2.5">
        <p className="mb-1.5 text-[11px] font-extrabold text-iflow-800">
          Supported by — the disclosure this claim was scored against
        </p>
        {ids.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {ids.map((id) => (
              <Link
                key={id}
                to={`/document/${id}`}
                title="Open in the Evidence lens"
                className="rounded-full border border-sky-200 bg-white px-2 py-0.5 text-[11px] text-gray-700 hover:border-iflow-300 hover:text-iflow-700"
              >
                📄 {documentNames?.[id] ?? 'Untitled document'}
              </Link>
            ))}
          </div>
        ) : (
          <p className="text-[11.5px] font-semibold text-amber-700">⚠ Asserted with no supporting document</p>
        )}
        <p className="mt-1.5 text-[11px] text-gray-500">{scoredAgainst}</p>
      </div>

      <PriorArtRuler rows={claim.prior_art ?? []} />

      <details className="mt-2 rounded-lg border border-sky-200 bg-slate-50 px-3 py-1.5" open>
        <summary className="cursor-pointer text-[12.5px] font-bold text-iflow-900">Why this score</summary>
        <p className="mt-1 text-[11.5px] leading-relaxed text-gray-700">
          {claim.assessment
            ? claim.assessment
            : 'The analysis recorded no reason for this claim. Re-run the analysis to generate one.'}
        </p>
      </details>

      <Link
        to="/projects?view=atlas"
        title="Opens My inventions with this invention on the atlas"
        className="mt-2 inline-block text-[12.5px] font-bold text-iflow-600 hover:text-iflow-700"
      >
        Where does this sit? →
      </Link>
    </aside>
  )
}
